import { useCallback, useEffect, useRef } from 'react';
import type { AuthUser } from '@/lib/api/auth';
import analyticsAPI from '@/lib/api/analytics';
import { detectBrowserName, detectDeviceType } from '@/lib/utils/analytics';
import {
  clearAuthenticatedSession,
  getOrCreateAuthenticatedSessionId,
  getStoredAuthenticatedSessionId,
  invalidateAuthenticatedSessionRegistration,
  isAuthenticatedSessionCreated,
  markAuthenticatedSessionCreated,
} from '@/lib/utils/analytics-session';
import logger from '@/lib/utils/logger';

const HEARTBEAT_INTERVAL_MS = 60_000;
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

type EndReason = 'logout' | 'tab_close' | 'timeout' | 'visibility_hidden' | 'navigation';

interface UseSessionLifecycleResult {
  getSessionId: () => string | null;
  endSession: (reason: EndReason) => Promise<void>;
}

/** Owns the authenticated analytics session for exactly one browser tab. */
export function useSessionLifecycle(user: AuthUser | null): UseSessionLifecycleResult {
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSessionActiveRef = useRef(false);
  const creationPromiseRef = useRef<Promise<boolean> | null>(null);
  const endPromiseRef = useRef<Promise<void> | null>(null);
  const pageExitRef = useRef(false);

  const getSessionId = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    return getOrCreateAuthenticatedSessionId();
  }, []);

  const ensureSession = useCallback(async (): Promise<boolean> => {
    if (user === null) return false;
    if (isAuthenticatedSessionCreated()) {
      isSessionActiveRef.current = true;
      return true;
    }
    if (creationPromiseRef.current !== null) return creationPromiseRef.current;

    const sessionId = getSessionId();
    if (sessionId === null) return false;

    creationPromiseRef.current = (async (): Promise<boolean> => {
      try {
        const response = await analyticsAPI.sessions.create({
          session_id: sessionId,
          device_type: detectDeviceType(navigator.userAgent),
          browser: detectBrowserName(navigator.userAgent),
          referrer: document.referrer,
        });
        if (!response.data.success) {
          logger.warn('Analytics session creation was not accepted');
          return false;
        }
        if (pageExitRef.current) return false;
        markAuthenticatedSessionCreated();
        isSessionActiveRef.current = true;
        return true;
      } catch (error: unknown) {
        logger.warn('Analytics session creation failed', error);
        return false;
      } finally {
        creationPromiseRef.current = null;
      }
    })();

    return creationPromiseRef.current;
  }, [getSessionId, user]);

  const sendHeartbeat = useCallback(async (): Promise<void> => {
    if (user === null) return;
    if (!isSessionActiveRef.current) {
      await ensureSession();
      return;
    }

    const sessionId = getSessionId();
    if (sessionId === null) return;

    try {
      const response = await analyticsAPI.sessions.heartbeat(sessionId);
      if (!response.data.session_found) {
        invalidateAuthenticatedSessionRegistration();
        isSessionActiveRef.current = false;
        await ensureSession();
      }
    } catch (error: unknown) {
      logger.warn('Analytics session heartbeat failed', error);
    }
  }, [ensureSession, getSessionId, user]);

  const deliverRegularEnd = useCallback(async (sessionId: string, reason: EndReason): Promise<boolean> => {
    const response = await analyticsAPI.sessions.end(sessionId, reason);
    return response.data.success;
  }, []);

  const endSession = useCallback(
    async (reason: EndReason): Promise<void> => {
      if (endPromiseRef.current !== null) return endPromiseRef.current;
      const isPageExit = reason === 'tab_close' || reason === 'navigation';
      if (isPageExit) {
        const sessionId = getStoredAuthenticatedSessionId();
        if (sessionId === null) return;

        pageExitRef.current = true;
        isSessionActiveRef.current = false;
        const beaconUrl = `/api/v1/analytics/sessions/${encodeURIComponent(sessionId)}/end/beacon?end_reason=${encodeURIComponent(reason)}`;
        const delivered = navigator.sendBeacon(beaconUrl);
        if (delivered) {
          clearAuthenticatedSession();
          return;
        }

        endPromiseRef.current = deliverRegularEnd(sessionId, reason)
          .then((fallbackDelivered) => {
            if (fallbackDelivered) {
              clearAuthenticatedSession();
            }
          })
          .catch((error: unknown) => {
            logger.warn('Analytics page-exit fallback failed', { reason, error });
          })
          .finally(() => {
            endPromiseRef.current = null;
          });
        return endPromiseRef.current;
      }

      const pendingCreation = creationPromiseRef.current;
      if (pendingCreation !== null) await pendingCreation;
      if (!isSessionActiveRef.current || !isAuthenticatedSessionCreated()) return;

      const sessionId = getSessionId();
      if (sessionId === null) return;
      isSessionActiveRef.current = false;

      endPromiseRef.current = (async (): Promise<void> => {
        let delivered = false;
        try {
          delivered = await deliverRegularEnd(sessionId, reason);
        } catch (error: unknown) {
          logger.warn('Analytics session end failed', { reason, error });
        } finally {
          if (delivered || reason === 'logout') {
            clearAuthenticatedSession();
          } else {
            isSessionActiveRef.current = true;
          }
          endPromiseRef.current = null;
        }
      })();

      return endPromiseRef.current;
    },
    [deliverRegularEnd, getSessionId]
  );

  const resetInactivityTimer = useCallback((): void => {
    if (user !== null && !isSessionActiveRef.current && endPromiseRef.current === null) {
      void ensureSession().then((active) => {
        if (active && heartbeatIntervalRef.current === null) {
          heartbeatIntervalRef.current = setInterval(() => {
            void sendHeartbeat();
          }, HEARTBEAT_INTERVAL_MS);
        }
      });
    }
    if (inactivityTimeoutRef.current !== null) clearTimeout(inactivityTimeoutRef.current);
    inactivityTimeoutRef.current = setTimeout(() => {
      if (isSessionActiveRef.current) {
        if (heartbeatIntervalRef.current !== null) clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
        void endSession('timeout');
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [endSession, ensureSession, sendHeartbeat, user]);

  const handleVisibilityChange = useCallback((): void => {
    if (document.hidden) {
      if (heartbeatIntervalRef.current !== null) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    if (user !== null) {
      void sendHeartbeat();
      heartbeatIntervalRef.current ??= setInterval(() => {
        void sendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);
      resetInactivityTimer();
    }
  }, [resetInactivityTimer, sendHeartbeat, user]);

  const handlePageHide = useCallback((): void => {
    if (heartbeatIntervalRef.current !== null) clearInterval(heartbeatIntervalRef.current);
    heartbeatIntervalRef.current = null;
    void endSession('tab_close');
  }, [endSession]);

  const handlePageShow = useCallback((): void => {
    pageExitRef.current = false;
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (user === null) {
      if (heartbeatIntervalRef.current !== null) clearInterval(heartbeatIntervalRef.current);
      if (inactivityTimeoutRef.current !== null) clearTimeout(inactivityTimeoutRef.current);
      heartbeatIntervalRef.current = null;
      inactivityTimeoutRef.current = null;
      return;
    }

    void ensureSession();
    heartbeatIntervalRef.current = setInterval(() => {
      void sendHeartbeat();
    }, HEARTBEAT_INTERVAL_MS);
    resetInactivityTimer();

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    return () => {
      if (heartbeatIntervalRef.current !== null) clearInterval(heartbeatIntervalRef.current);
      if (inactivityTimeoutRef.current !== null) clearTimeout(inactivityTimeoutRef.current);
      heartbeatIntervalRef.current = null;
      inactivityTimeoutRef.current = null;
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [
    ensureSession,
    handlePageHide,
    handlePageShow,
    handleVisibilityChange,
    resetInactivityTimer,
    sendHeartbeat,
    user,
  ]);

  return { getSessionId, endSession };
}

export default useSessionLifecycle;
